import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Icon, Text } from '@kognitos/lattice';
import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import type { DefaultFileUploadAreaProps } from '../../types';
import { FileCard } from './FileCard';

const DefaultFileUploadDialog = lazy(() =>
  import('./DefaultFileUploadDialog').then(m => ({
    default: m.DefaultFileUploadDialog,
  })),
);

/**
 * Alternative file upload area for single default file inputs with history.
 *
 * Renders an inline layout with the file card on the left and an "Upload files"
 * button on the right within a dashed border area. When no file is present, shows
 * only the upload button. Displays a message about overriding the default value.
 * Clicking the upload button opens a dialog for uploading or selecting from history.
 *
 * @param props - Component props
 * @returns JSX element containing the default file upload layout
 */
export function DefaultFileUploadArea({
  uploadedFiles,
  onRemove,
  disabled,
  fileHistory,
  onSelectHistoryFile,
  error,
}: DefaultFileUploadAreaProps) {
  const { t } = useTranslation('automation-inputs');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const hasFiles = uploadedFiles.length > 0;
  const file = uploadedFiles[0];

  /**
   * Opens the upload dialog when the upload button is clicked.
   */
  const handleUploadClick = () => {
    setIsDialogOpen(true);
  };

  /**
   * Handles saving a file from the upload dialog.
   * Passes the selected or uploaded file value to the parent via onSelectHistoryFile.
   *
   * @param value - The Commonv1Value of the selected/uploaded file
   */
  const handleDialogSave = (value: Commonv1Value) => {
    onSelectHistoryFile?.(value);
  };

  const getBorderClass = () => {
    if (error) return 'border-destructive';
    return 'border-border';
  };

  return (
    <Flex direction="column" gap={2} className="w-full">
      <Flex
        align="center"
        gap={2}
        className={`w-full bg-muted border border-dashed ${getBorderClass()} rounded-md p-2`}
      >
        {/* File card - shown when a file exists */}
        {hasFiles && file && (
          <div className="flex-1 min-w-0">
            <FileCard
              file={file}
              onRemove={() => onRemove(file.resourceName)}
              showRemove={!disabled}
              fileHistory={fileHistory}
              onSelectHistoryFile={onSelectHistoryFile}
            />
          </div>
        )}

        {/* Upload files button */}
        {!disabled && (
          <Flex
            align="center"
            justify="center"
            gap={2}
            className="flex-1 cursor-pointer rounded-md px-8 py-2 transition-colors hover:bg-accent"
            onClick={handleUploadClick}
            role="button"
            tabIndex={0}
            aria-label={t('actions.uploadFiles')}
          >
            <Icon type="Upload" size="sm" />
            <Text level="base" weight="medium">
              {t('actions.uploadFiles')}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Default override message */}
      <Text level="base" color="muted">
        {t('messages.defaultOverride')}
      </Text>

      {/* Upload dialog */}
      {isDialogOpen && (
        <Suspense fallback={null}>
          <DefaultFileUploadDialog
            isOpen={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            fileHistory={fileHistory ?? []}
            onSave={handleDialogSave}
          />
        </Suspense>
      )}
    </Flex>
  );
}
